import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaddySidebar } from "../components/CaddySidebar";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import RelayCalculatorPage from "./RelayCalculatorPage";
import { RelayLoader } from "../components/RelayLoader";

const API = process.env.REACT_APP_BACKEND_URL;
const COLOR = { bg: "var(--pr-bg)", ink2: "var(--pr-ink2)" };

const ONBOARDING_STEPS = ["Calculate", "Sync your sheet", "Log your race"];

function OnboardingBanner({ hasTarget }) {
  return (
    <div className="mx-4 lg:mx-0 mt-4 mb-2 rounded-2xl p-4 sm:p-5" style={{ background: "var(--pr-ink-surface)" }} data-testid="onboarding-calc-banner">
      <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--pr-gold)" }}>
        Step 1 of 3 · Calculate your relay numbers
      </p>
      <p className="text-sm mt-1.5 leading-relaxed" style={{ color: "rgba(255,255,255,0.8)" }}>
        Everything in here measures against one number: what you need to earn this week.
        {hasTarget ? " Re-run it if anything changed." : " Takes about three minutes."}
      </p>
      <ol className="flex flex-wrap gap-2 mt-3" data-testid="onboarding-calc-steps">
        {ONBOARDING_STEPS.map((label, i) => (
          <li
            key={label}
            className="text-[11px] font-bold px-2.5 py-1 rounded-full"
            style={i === 0
              ? { background: "var(--pr-gold)", color: "var(--pr-on-gold)" }
              : { background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)" }}
            data-testid={`onboarding-calc-step-${i + 1}`}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function CaddyCalculatorPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [view, setView] = useState("loading");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/summary`, { credentials: "include" });
        if (res.status === 401 || res.status === 403) {
          navigate("/app");
          return;
        }
        if (!res.ok) {
          setView("error");
          return;
        }
        setSummary(await res.json());
        setView("ready");
      } catch {
        setView("error");
      }
    })();
  }, [navigate]);

  const signOut = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      // noop
    }
    navigate("/app");
  };

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }} data-testid="caddy-calculator-loading">
        <RelayLoader label="Opening the Relay Calculator…" />
      </div>
    );
  }

  const params = new URLSearchParams(window.location.search);
  const fromSeasonSetup = params.get("from") === "season-setup";
  const seasonSetupActive = fromSeasonSetup && !!summary?.season_setup;
  const onboardingMode = !seasonSetupActive && params.get("onboarding") === "1";
  const pitStopMode = !seasonSetupActive && params.get("from") === "pit-stop";
  const hasTarget = summary?.weekly_target != null && summary.weekly_target > 0;
  // The mid-quarter warning only belongs to racers who already locked a target and are
  // part-way through a quarter. During a Pit Stop, Season Setup or onboarding, changing
  // the numbers is the whole point, so it stays out of the way.
  const midQuarterChange = hasTarget && !!summary?.quarter && !summary.quarter.prompt
    && !pitStopMode && !seasonSetupActive && !onboardingMode;

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }} data-testid="caddy-calculator-page">
      <div className="w-full max-w-7xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8 pb-28 lg:pb-10">
        <CaddySidebar active="calculator" sheetUrl={summary?.sheet_url} onSignOut={signOut} />
        <main>
          {seasonSetupActive && (
            <div className="mx-4 lg:mx-0 mt-4 mb-2 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" style={{ background: "var(--pr-ink-surface)" }} data-testid="season-setup-calc-banner">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--pr-gold)" }}>Season Setup, Recalibrate My Relay</p>
                <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.75)" }}>
                  Rework your plan below, savings, debt, PTO, taxes. When it feels right, hit "Start My Payday Relay Race" in your results.
                </p>
              </div>
              <a href="/app?season-setup=1" className="text-xs font-bold underline" style={{ color: "var(--pr-gold)" }} data-testid="season-setup-calc-back">← Back to Season Setup</a>
            </div>
          )}
          {onboardingMode && <OnboardingBanner hasTarget={hasTarget} />}
          {pitStopMode && (
            <div className="mx-4 lg:mx-0 mt-4 mb-2 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" style={{ background: "var(--pr-ink-surface)" }} data-testid="pit-stop-calc-banner">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--pr-gold)" }}>Pit Stop, recalibrate your relay numbers</p>
                <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.75)" }}>
                  Rework your pace for the quarter ahead. When it feels right, hit "Lock in my new numbers" in your results and we'll carry the target back into your quarterly tax day.
                </p>
              </div>
              <a href="/app/reset" className="text-xs font-bold underline" style={{ color: "var(--pr-gold)" }} data-testid="pit-stop-calc-back">← Back to Pit Stop</a>
            </div>
          )}
          <div
            data-theme="light"
            className="lg:rounded-3xl lg:overflow-hidden"
            style={{ background: "#FAF7F0", color: "#2A1E5C" }}
            data-testid="calculator-worksheet"
          >
            <RelayCalculatorPage embedded seasonSetupMode={seasonSetupActive} onboardingMode={onboardingMode} pitStopMode={pitStopMode} midQuarterChange={midQuarterChange} quarterKey={summary?.quarter?.key || ''} hasSeasonPass={(summary?.entitlements?.mode || (summary?.race_context?.is_sprint ? "sprint" : "season")) === "season"} />
          </div>
        </main>
      </div>
      <CaddyBottomNav active="calculator" />
    </div>
  );
}
